/**
 * @fileoverview Audio upload utilities.
 *
 * Provides client-side validation and metadata extraction for audio uploads
 * before sending to the backend.
 */

/**
 * Allowed audio MIME types.
 * Supports: MP3, WAV, AAC/M4A, FLAC, OGG
 */
const ALLOWED_AUDIO_MIME_TYPES = [
  // MP3
  'audio/mpeg',
  'audio/mp3',
  // WAV
  'audio/wav',
  'audio/wave',
  'audio/x-wav',
  // AAC / M4A
  'audio/aac',
  'audio/mp4',
  'audio/x-m4a',
  'audio/m4a',
  // FLAC
  'audio/flac',
  'audio/x-flac',
  // OGG
  'audio/ogg',
  'audio/vorbis',
] as const

/**
 * Allowed audio file extensions.
 */
const ALLOWED_AUDIO_EXTENSIONS = ['.mp3', '.wav', '.aac', '.m4a', '.flac', '.ogg'] as const

/**
 * Validation result for an audio file.
 */
export interface AudioValidationResult {
  valid: boolean
  error?: string
}

/**
 * Extracted audio metadata.
 */
export interface ExtractedAudioMetadata {
  duration: number // in seconds
}

/**
 * Validates an audio file for upload.
 * Checks file extension and MIME type.
 *
 * @param file - The file to validate
 * @returns Validation result with error message if invalid
 */
export function validateAudioFile(file: File): AudioValidationResult {
  // Check file extension
  const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
  if (!ALLOWED_AUDIO_EXTENSIONS.includes(extension as (typeof ALLOWED_AUDIO_EXTENSIONS)[number])) {
    return {
      valid: false,
      error: `Invalid audio format. Allowed: ${ALLOWED_AUDIO_EXTENSIONS.join(', ')}`,
    }
  }

  // Check MIME type (lenient - some browsers report different MIME types)
  if (!ALLOWED_AUDIO_MIME_TYPES.includes(file.type as (typeof ALLOWED_AUDIO_MIME_TYPES)[number])) {
    // Log warning but don't fail - extension check is more reliable
    console.warn(`[Audio] MIME type ${file.type} not in allowlist, but extension ${extension} is valid`)
  }

  return { valid: true }
}

/**
 * Extracts metadata from an audio file using HTML5 audio element.
 *
 * @param file - The audio file to extract metadata from
 * @returns Promise resolving to extracted metadata
 * @throws Error if audio cannot be loaded or is corrupt
 */
export function extractAudioMetadata(file: File): Promise<ExtractedAudioMetadata> {
  return new Promise((resolve, reject) => {
    const audio = document.createElement('audio')
    const objectUrl = URL.createObjectURL(file)

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl)
      audio.remove()
    }

    audio.addEventListener('loadedmetadata', () => {
      const metadata: ExtractedAudioMetadata = {
        duration: audio.duration,
      }
      cleanup()
      resolve(metadata)
    })

    audio.addEventListener('error', () => {
      cleanup()
      reject(new Error('Failed to load audio file. The file may be corrupt or in an unsupported format.'))
    })

    // Set a timeout for loading
    const timeout = setTimeout(() => {
      cleanup()
      reject(new Error('Audio loading timed out. The file may be too large or corrupt.'))
    }, 30000) // 30 second timeout

    audio.addEventListener('loadedmetadata', () => clearTimeout(timeout))
    audio.addEventListener('error', () => clearTimeout(timeout))

    audio.preload = 'metadata'
    audio.src = objectUrl
  })
}

/**
 * Checks if a file is an audio file based on MIME type.
 *
 * @param file - The file to check
 * @returns True if the file is an audio file
 */
export function isAudioFile(file: File): boolean {
  return file.type.startsWith('audio/')
}

/**
 * Checks if a DataTransfer contains audio files.
 *
 * @param dataTransfer - The DataTransfer from drag event
 * @returns True if at least one audio file is present
 */
export function hasAudioFiles(dataTransfer: DataTransfer): boolean {
  if (dataTransfer.types.includes('Files')) {
    for (let i = 0; i < dataTransfer.items.length; i++) {
      const item = dataTransfer.items[i]
      if (item.kind === 'file' && item.type.startsWith('audio/')) {
        return true
      }
    }
  }
  return false
}

/**
 * Gets the first audio file from a DataTransfer.
 *
 * @param dataTransfer - The DataTransfer from drag event
 * @returns The first audio file, or null if none found
 */
export function getAudioFileFromDataTransfer(dataTransfer: DataTransfer): File | null {
  if (dataTransfer.files.length > 0) {
    for (let i = 0; i < dataTransfer.files.length; i++) {
      const file = dataTransfer.files[i]
      if (isAudioFile(file)) {
        return file
      }
    }
  }
  return null
}

/**
 * Gets the media type from a DataTransfer.
 * Useful for determining which upload handler to use.
 *
 * Note: During drag operations, browsers often don't provide MIME types for security.
 * This function falls back to checking 'public.file-url' (macOS) and assumes video
 * as the most common case when file type can't be determined.
 *
 * @param dataTransfer - The DataTransfer from drag event
 * @returns 'video', 'audio', 'image', or null if no media files
 */
export function getMediaTypeFromDataTransfer(dataTransfer: DataTransfer): 'video' | 'audio' | 'image' | null {
  if (dataTransfer.types.includes('Files')) {
    // First try to get type from MIME (available in some browsers)
    for (let i = 0; i < dataTransfer.items.length; i++) {
      const item = dataTransfer.items[i]
      if (item.kind === 'file') {
        if (item.type.startsWith('video/')) {
          return 'video'
        }
        if (item.type.startsWith('audio/')) {
          return 'audio'
        }
        if (item.type.startsWith('image/')) {
          return 'image'
        }
      }
    }

    // Fallback: Check if we have files but couldn't determine type
    // This happens on macOS Finder drags where MIME isn't available until drop
    // In this case, assume it could be media and return 'video' as default
    // The actual type will be validated on drop
    for (let i = 0; i < dataTransfer.items.length; i++) {
      const item = dataTransfer.items[i]
      if (item.kind === 'file') {
        // If there's a file but no MIME type, assume it could be video
        // This enables the drop preview; actual validation happens on drop
        return 'video'
      }
    }
  }

  // macOS Finder sometimes uses 'public.file-url' for file drags
  if (dataTransfer.types.includes('public.file-url')) {
    return 'video' // Assume video, will be validated on drop
  }

  return null
}
